<template>
    <div>
        <FilterBadges :badges="filterBadges" class="mb-1"></FilterBadges>
        <div v-if="clamped || codes.length > 1" class="d-flex align-center flex-wrap">
            <p v-if="clamped" class="graficas-nota">Sin rango de fechas se muestran los últimos {{ maxDays }} días.</p>
            <v-spacer></v-spacer>
            <CurrencyToggle v-model="selected" :codes="codes"></CurrencyToggle>
        </div>
        <v-row dense class="mt-1">
            <v-col v-for="kpi in kpis" :key="kpi.title" cols="12" :sm="12 / kpis.length">
                <StatCard :tone="kpi.tone" :icon="kpi.icon" :title="kpi.title" :value="kpi.value"
                    :description="kpi.description" :loading="loading"></StatCard>
            </v-col>
        </v-row>
        <v-row dense>
            <v-col v-for="panel in panels" :key="panel.title" cols="12" :md="panel.cols">
                <ChartPanel :title="panel.title" :type="panel.type" :labels="panel.chart.labels"
                    :data="panel.chart.data" :options="panel.options" :loading="loading" :error="error"></ChartPanel>
            </v-col>
        </v-row>
    </div>
</template>
<script>
import StatCard from '@/components/shared/StatCard.vue';
import DashboardDataService from '@/service/dashboard/DashboardDataService';
import { dashboardFilterMixin, dashboardRangeMixin, dashboardWidgetMixin } from '@/mixins/dashboardWidgetMixin';
import ChartPanel from './ChartPanel.vue';
import CurrencyToggle from './CurrencyToggle.vue';
import FilterBadges from './FilterBadges.vue';
import {
    MAX_DAYS,
    chartFor,
    currencyCodes,
    dayChartFor,
    formatMoney,
    kindChartFor,
    moneyChartOptions,
    pickCurrency,
    summaryFor
} from './dashboardData';

/**
 * Dinero de ventas del periodo: ventas, por cobrar, ticket promedio y desgloses por día, forma de pago, cliente y
 * sucursal. Todo lo agrupa el backend (`api/v1/dashboard/ventas`), ya sin ventas canceladas, merma ni traslados, y
 * separado por moneda: aquí solo se elige qué moneda mostrar.
 */
export default {
    name: 'GraficaDinero',
    components: { ChartPanel, CurrencyToggle, FilterBadges, StatCard },
    mixins: [dashboardWidgetMixin, dashboardRangeMixin, dashboardFilterMixin],
    filterScope: {
        sucursal: true,
        producto: true,
        partial: {
            producto: [
                {
                    label: 'Importes del producto',
                    reason: 'Un ticket mezcla productos: con el filtro se suma lo vendido de ese producto, no el total del ticket.'
                },
                {
                    label: 'Sin crédito ni tipo',
                    reason: 'El saldo es por ticket y un solo producto sería una sola rebanada: se ocultan "Crédito sin cobrar" y "Productos vs servicios".'
                }
            ]
        }
    },
    props: {
        // Roles de todas las sucursales (CAP.DASHBOARD_TODAS_SUCURSALES_VER). Sin ello solo se ve lo de la propia
        // sucursal: no hay top de clientes, comparativo por sucursal ni "por cobrar" (el backend ni los manda).
        allBranches: { type: Boolean, default: false }
    },
    data() {
        return { data: null, selected: '' };
    },
    computed: {
        maxDays() {
            return MAX_DAYS;
        },
        resumen() {
            return this.data ? this.data.resumen : [];
        },
        codes() {
            return currencyCodes(this.resumen);
        },
        currency() {
            return pickCurrency(this.codes, this.selected);
        },
        summary() {
            return summaryFor(this.resumen, this.currency);
        },
        chartOptions() {
            return moneyChartOptions(this.currency);
        },
        kpis() {
            const show = (value) => (this.error ? null : formatMoney(value, this.currency));
            const cards = [
                { title: 'Ventas del periodo', value: show(this.summary.total), tone: 'success', icon: 'fas fa-sack-dollar', description: this.error ? '' : `${this.summary.tickets} tickets` },
                // Solo lo que quedó sin pagar de las ventas de este periodo. El total adeudado a hoy (cualquier fecha) es
                // el widget "Cuentas por cobrar".
                { title: 'Crédito sin cobrar', value: show(this.summary.owed), tone: 'warning', icon: 'fas fa-hand-holding-dollar', description: this.error ? '' : 'de las ventas del periodo', onlyAllBranches: true, hideWithProduct: true },
                { title: 'Ticket promedio', value: show(this.summary.average), tone: 'info', icon: 'fas fa-receipt', description: this.error ? '' : this.filters.productoId ? 'del producto ÷ tickets con él' : 'venta total ÷ tickets' }
            ];
            return cards.filter((card) => (this.allBranches || !card.onlyAllBranches) && !(this.filters.productoId && card.hideWithProduct));
        },
        panels() {
            const d = this.data || {};
            const code = this.currency;
            const options = this.chartOptions;
            const panels = [
                { title: 'Venta por día', type: 'line', cols: 8, chart: dayChartFor(d.porDia, code, this.week), options: options.vertical },
                { title: 'Por forma de pago', type: 'doughnut', cols: 4, chart: chartFor(d.porFormaPago, code), options: options.donut },
                { title: 'Productos vs servicios', type: 'doughnut', cols: 4, chart: kindChartFor(d.porTipo, code), options: options.donut, hideWithProduct: true },
                { title: 'Top 5 clientes', type: 'horizontalBar', cols: 4, chart: chartFor(d.topClientes, code), options: options.horizontal, onlyAllBranches: true },
                { title: 'Venta por sucursal', type: 'bar', cols: 4, chart: chartFor(d.porSucursal, code), options: options.vertical, onlyAllBranches: true }
            ];
            return panels.filter((panel) => (this.allBranches || !panel.onlyAllBranches) && !(this.filters.productoId && panel.hideWithProduct));
        }
    },
    created() {
        this.reload();
    },
    methods: {
        reload() {
            return this.loadWidget(() => DashboardDataService.ventas(this.startNatural, this.endNatural, this.filters), (data) => {
                this.data = data;
            });
        }
    }
};
</script>
<style scoped>
.graficas-nota {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--app-text-muted);
}
</style>
